"use client";

import { Favorite1 } from "@ellaandil/ui";
import { useAddToCart, useRemoveFromCart } from "@trydig/centra/client";
import { Checkbox } from "@/components/ui/form/Checkbox";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { Link, productCanonicalPath } from "@/i18n/routing";
import { DONATION_ITEM_ID, useCart, useDonationAvailability } from "./hooks";

// The donation product's own page, linked from "Read more" for the full story.
const DONATION_PRODUCT_URI = "stine-sofies-stiftelse";

// The foundation's own site, linked from the bolded name in the description.
const DONATION_FOUNDATION_URL = "https://www.stinesofiesstiftelse.no/";

// Fixed price of the donation product, used as the display amount before it's
// actually in the cart (once added, the real formatted price from the
// selection is used instead, in case pricing ever changes per market).
const DONATION_FALLBACK_PRICE = "10 NOK";

/**
 * Promo card, shown above the summary whenever the cart has other items in
 * it, that lets the customer add/remove the Stine Sofie Stiftelsen donation
 * product. Checking it adds the item to the selection; unchecking removes it.
 * State is derived from the cart itself, so the toggle stays in sync if the
 * line is instead removed via `CartSummary`'s donation row.
 */
export const DonationToggle = () => {
	const t = useTranslations();
	const { data } = useCart();
	const { data: isAvailable } = useDonationAvailability();
	const addToCart = useAddToCart();
	const removeFromCart = useRemoveFromCart();

	const donationLine = data?.donation?.line;
	const isChecked = !!donationLine;
	const isPending = addToCart.isPending || removeFromCart.isPending;
	const donationPrice = data?.donation?.priceEach ?? DONATION_FALLBACK_PRICE;

	// Only makes sense as an add-on to an actual order — hide it once the cart
	// (excluding the donation itself) has nothing else in it. Also hide it in
	// markets where the donation product itself isn't available to buy (and
	// while that's still being checked, to avoid a flash of an interactive
	// checkbox that then disappears).
	if (!data?.items?.length || isAvailable !== true) {
		return null;
	}

	const handleChange = (checked: boolean) => {
		if (checked) {
			addToCart.mutate({ itemId: DONATION_ITEM_ID });
		} else if (donationLine) {
			removeFromCart.mutate({ line: donationLine });
		}
	};

	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs rounded-general-md bg-[#D6E7F5B2] p-grid-component-spacing-md">
			<div className="flex items-center gap-grid-component-spacing-3xs">
				<Favorite1 className="size-5" aria-hidden="true" />
				<Typography variant="body-sm-bold" as="h3" className="text-text-dark-text-1">
					{t("Add a donation")}
				</Typography>
			</div>
			<Typography variant="body-xs-reg" className="text-text-dark-text-2">
				{t("{{amount}} from this purchase goes to ", { amount: donationPrice })}{" "}
				<a
					href={DONATION_FOUNDATION_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="font-bold text-text-dark-text-1"
				>
					{t("Stine Sofies Foundation")}
				</a>{" "}
				{t("— for a safe childhood, free from violence.")}
			</Typography>
			<div className="flex items-center justify-between">
				<Checkbox
					size="large"
					isSelected={isChecked}
					isDisabled={isPending}
					onChange={handleChange}
				>
					{donationPrice}
				</Checkbox>
				<Link href={productCanonicalPath(DONATION_PRODUCT_URI)} className="shrink-0">
					<Typography
						variant="body-xs-reg"
						as="span"
						className="text-text-dark-text-1 underline decoration-solid underline-offset-[0.2em]"
					>
						{t("Read more")}
					</Typography>
				</Link>
			</div>
		</div>
	);
};
